/**
 * Follow-up drawer — the queue model (drawer redesign Phase 6a, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-drawer-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * ── WHAT IT REPLACES ───────────────────────────────────────────────────────────────────────────
 * Today's drawer, src/pages/Admin/components/CreateFollowup.vue (FROZEN), answers "which queue am
 * I?" with ~30 separate `switch (this.detailPageName)` blocks and reads its data out of the
 * caller's query result by POSITION (`this.dataToDisplay[n]`), with a different position per queue.
 * Every value below is copied from those blocks. cypress/node-tests/followups-drawer-queues.test.js
 * reads them back OUT OF CreateFollowup.vue's code (comments stripped) and fails on any difference.
 *
 * ── FIXED IN THE NEW DRAWER (Nilay 2026-10-02, drawer plan "6a notes" bug 1) ─────────────────
 *   • Broken appointments: today's drawer GUARDS the auto frequency on BROKEN_APPOINTMENTS_AUTO_FREQUENCY
 *     but READS BROKEN_APPOINTMENTS_FREQUENCY (CreateFollowup.vue:2728). Settings.vue (:4743) writes
 *     only BROKEN_APPOINTMENTS_AUTO_FREQUENCY, so on an office that set an auto frequency the legacy
 *     read throws. Here guard and read are the key Settings writes; the legacy read key is kept as
 *     `autoFrequencyLegacy` so followups-drawer-queues.test.js still binds this entry to
 *     CreateFollowup.vue's code.
 *
 * ── QUIRKS KEPT ON PURPOSE (today's behaviour, not a typo here) ────────────────────────────────
 *   • Outstanding claims: max follow-ups is GUARDED by OUTSTANDING_CLAIMS_FOLLOWUPS but READ from
 *     OUTSTANDING_CLAIMS_MAX_FOLLOWUPS (:3262). Settings.vue writes neither, so claims never have a
 *     maximum today.
 *   • Outstanding claims: the auto frequency is OUTSTANDING_CLAIM_FOLLOWUP_DURATION + ' ' +
 *     OUTSTANDING_CLAIM_FOLLOWUP_DURATION_TYPE (:2731–2732), not an *_AUTO_FREQUENCY key.
 *   • Processed pre-auths: the patient is ROW 2 of result set 0 (:3612) and the follow-up activity
 *     is read from set 1 (:3761) while the tasks are set 4 (:3744).
 *   • Copy-to-commlog: a save sends '' when the setting is absent (:2669–2675); editing a note sends
 *     'TRUE' when absent (:2775). Same keys, different defaults.
 *   • The "texted" / "emailed" status lookups exist only for recall, unscheduled, broken and
 *     balances (:3135–3163); every other queue answers `false` ("no mapping").
 */

/** CreateFollowup's `detailPageName` values, in its switch order. */
export const DETAIL_PAGE_NAMES = [
  'OverdueRecallDetails',
  'unscheduledPatientDetails',
  'BrokenAppointmentDetails',
  'AgingDetails',
  'OutstandingPreAuthDetails',
  'ProcessedPreAuthDetails',
  'PatientBalancesDetails',
];

/**
 * Field by field (CreateFollowup.vue method / computed in brackets):
 *   taskTypeID            [getTaskTypeID]
 *   followupType          [getFollowupType] — also the SMS/email/payment/AI gates' vocabulary
 *   typeAllCaps           [getTaskTypeAllCaps] — the save payload's `type`
 *   title                 [printTitleOfFollowUp]
 *   statusField           [statusPropertyName] — the task column the chosen status is written to
 *   statusLabelField      [getStatus / selectedFollowupStatusName] — the status row's label column
 *   statusUpdatedAtField  [statusUpdatedAtPropertyName]
 *   settings.*            every settings key the drawer reads for this queue (see each quirk above)
 *   resultSets.*          the `dataToDisplay[n]` the drawer reads; `{ set, row }` for a single row;
 *                         `guardSet` when the length check is on a DIFFERENT set than the read
 *   smsTemplateTag        [SMSWikiPagesForFollowup] — undefined ⇒ no SMS template can ever match
 *   textedStatusLookup    [smsStatusID / emailStatusID] — false ⇒ the lookup returns `false`
 */
export const DRAWER_QUEUES = {
  OverdueRecallDetails: {
    detailPageName: 'OverdueRecallDetails',
    taskTypeID: 3,
    followupType: 'Recall',
    typeAllCaps: 'OVERDUE_RECALL',
    title: 'Overdue Recall',
    statusField: 'OverdueRecallStatusID',
    statusLabelField: 'OverdueRecallStatus',
    statusUpdatedAtField: 'OverdueRecallStatusUpdatedAt',
    settings: {
      responsible: 'OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID',
      maxFollowupsGuard: 'OVERDUE_RECALL_MAX_FOLLOWUPS',
      maxFollowups: 'OVERDUE_RECALL_MAX_FOLLOWUPS',
      autoFrequencyGuard: 'OVERDUE_RECALL_AUTO_FREQUENCY',
      autoFrequency: 'OVERDUE_RECALL_AUTO_FREQUENCY',
      customFrequencyPrefix: 'OVERDUE_RECALL_FOLLOWUP_',
      copyToCommLog: 'OVERDUE_RECALL_COPY_TO_COMMLOG',
      editNoteCopyToCommLog: 'OVERDUE_RECALL_COPY_TO_COMMLOG',
      copyToClaimHistory: null,
    },
    resultSets: {
      patient: { set: 0, row: 0 },
      familyAppts: 1,
      insurance: 2,
      familyMembers: 3,
      tasks: 4,
      activities: 4,
      statuses: 5,
      patientList: 8,
      commlog: 9,
      pastAppointments: 10,
      autoRecallReminders: 11,
      familyPendingTx: 12,
      scheduledAppointments: 13,
      pendingTx: 14,
    },
    smsTemplateTag: 'recall-follow-up-sms-template',
    textedStatusLookup: true,
  },
  unscheduledPatientDetails: {
    detailPageName: 'unscheduledPatientDetails',
    taskTypeID: 4,
    followupType: 'Unscheduled Treatment',
    typeAllCaps: 'UNSCHEDULED_TREATMENT',
    title: 'Unscheduled Treatment',
    statusField: 'UnscheduledTreatmentStatusID',
    statusLabelField: 'UnscheduledTreatmentStatus',
    statusUpdatedAtField: 'UnscheduledTreatmentStatusUpdatedAt',
    settings: {
      responsible: 'UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID',
      maxFollowupsGuard: 'UNSCHEDULED_TREATMENT_MAX_FOLLOWUPS',
      maxFollowups: 'UNSCHEDULED_TREATMENT_MAX_FOLLOWUPS',
      autoFrequencyGuard: 'UNSCHEDULED_TREATMENT_AUTO_FREQUENCY',
      autoFrequency: 'UNSCHEDULED_TREATMENT_AUTO_FREQUENCY',
      customFrequencyPrefix: 'UNSCHEDULED_TREATMENT_FOLLOWUP_',
      copyToCommLog: 'UNSCHEDULED_TREATMENT_COPY_TO_COMMLOG',
      editNoteCopyToCommLog: 'UNSCHEDULED_TREATMENT_COPY_TO_COMMLOG',
      copyToClaimHistory: null,
    },
    resultSets: {
      patient: { set: 0, row: 0 },
      tasks: 3,
      activities: 3,
      statuses: 4,
      txInPlan: 5,
      scheduledAppointments: 6,
      plannedAppointments: 7,
      patientList: 10,
      commlog: 11,
      familyRecallAppointments: 12,
      familyPendingTx: 13,
    },
    smsTemplateTag: 'unscheduled-tx-follow-up-sms-template',
    textedStatusLookup: true,
  },
  BrokenAppointmentDetails: {
    detailPageName: 'BrokenAppointmentDetails',
    taskTypeID: 8,
    followupType: 'Broken Appointment',
    typeAllCaps: 'BROKEN_APPOINTMENTS',
    title: 'Broken Appointment',
    statusField: 'BrokenAppointmentStatusID',
    statusLabelField: 'BrokenAppointmentStatus',
    statusUpdatedAtField: 'BrokenAppointmentStatusUpdatedAt',
    settings: {
      responsible: 'BROKEN_APPOINTMENTS_RESPONSIBLE_EMPLOYEEID',
      maxFollowupsGuard: 'BROKEN_APPOINTMENTS_MAX_FOLLOWUPS',
      maxFollowups: 'BROKEN_APPOINTMENTS_MAX_FOLLOWUPS',
      autoFrequencyGuard: 'BROKEN_APPOINTMENTS_AUTO_FREQUENCY',
      autoFrequency: 'BROKEN_APPOINTMENTS_AUTO_FREQUENCY', // bug 1 fixed — the key Settings.vue writes
      autoFrequencyLegacy: 'BROKEN_APPOINTMENTS_FREQUENCY', // what CreateFollowup.vue:2728 reads (nothing writes it)
      customFrequencyPrefix: 'BROKEN_APPOINTMENTS_FOLLOWUP_',
      copyToCommLog: 'BROKEN_APPOINTMENTS_COPY_TO_COMMLOG',
      editNoteCopyToCommLog: 'BROKEN_APPOINTMENTS_COPY_TO_COMMLOG',
      copyToClaimHistory: null,
    },
    resultSets: {
      patient: { set: 0, row: 0, guardSet: 0 },
      brokenHistory: 2,
      tasks: 3,
      activities: 3,
      statuses: 4,
      commlog: 6,
    },
    smsTemplateTag: 'broken-apt-follow-up-sms-template',
    textedStatusLookup: true,
  },
  AgingDetails: {
    detailPageName: 'AgingDetails',
    taskTypeID: 2,
    followupType: 'Outstanding Claim',
    typeAllCaps: 'OUTSTANDING_CLAIMS',
    title: 'Outstanding Claim',
    statusField: 'OutstandingClaimStatusID',
    statusLabelField: 'OutstandingClaimStatus',
    statusUpdatedAtField: 'OutstandingClaimStatusUpdatedAt',
    settings: {
      responsible: 'OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID',
      maxFollowupsGuard: 'OUTSTANDING_CLAIMS_FOLLOWUPS', // ⚠ quirk, CreateFollowup.vue:3262
      maxFollowups: 'OUTSTANDING_CLAIMS_MAX_FOLLOWUPS',
      autoFrequencyDuration: 'OUTSTANDING_CLAIM_FOLLOWUP_DURATION',
      autoFrequencyDurationType: 'OUTSTANDING_CLAIM_FOLLOWUP_DURATION_TYPE',
      customFrequencyPrefix: 'OUTSTANDING_CLAIMS_FOLLOWUP_',
      copyToCommLog: 'OUTSTANDING_CLAIM_COPY_TO_COMMLOG',
      editNoteCopyToCommLog: 'OUTSTANDING_CLAIM_COPY_TO_COMMLOG', // remapped from OUTSTANDING_CLAIMS_…, :2772
      copyToClaimHistory: { payloadKey: 'ocCopyToClaimHistory', setting: 'OUTSTANDING_CLAIM_COPY_TO_OD_CLAIM_HISTORY' },
    },
    resultSets: {
      patient: { set: 0, row: 0, guardSet: 0 },
      claim: { set: 0, row: 0, guardSet: 0 },
      tasks: 1,
      activities: 1,
      assignment: { set: 1, row: 0 },
      statuses: { set: 2, guardSet: 0 },
      commlog: 5,
      claimStatusHistory: 6,
    },
    smsTemplateTag: undefined,
    textedStatusLookup: false,
  },
  OutstandingPreAuthDetails: {
    detailPageName: 'OutstandingPreAuthDetails',
    taskTypeID: 5,
    followupType: 'Outstanding PreAuth',
    typeAllCaps: 'OUTSTANDING_PREAUTHS',
    title: 'Outstanding Pre-Authorization',
    statusField: 'OutstandingPreAuthStatusID',
    statusLabelField: 'OutstandingPreAuthStatus',
    statusUpdatedAtField: 'OutstandingPreAuthStatusUpdatedAt',
    settings: {
      responsible: 'OUTSTANDING_PREAUTHS_RESPONSIBLE_EMPLOYEEID',
      maxFollowupsGuard: 'OUTSTANDING_PREAUTHS_MAX_FOLLOWUPS',
      maxFollowups: 'OUTSTANDING_PREAUTHS_MAX_FOLLOWUPS',
      autoFrequencyGuard: 'OUTSTANDING_PREAUTHS_AUTO_FREQUENCY',
      autoFrequency: 'OUTSTANDING_PREAUTHS_AUTO_FREQUENCY',
      customFrequencyPrefix: 'OUTSTANDING_PREAUTHS_FOLLOWUP_',
      copyToCommLog: 'OUTSTANDING_PREAUTHS_COPY_TO_COMMLOG',
      editNoteCopyToCommLog: 'OUTSTANDING_PREAUTHS_COPY_TO_COMMLOG',
      copyToClaimHistory: { payloadKey: 'opCopyToClaimHistory', setting: 'OUTSTANDING_PREAUTHS_COPY_TO_OD_CLAIM_HISTORY' },
    },
    resultSets: {
      patient: { set: 0, row: 0 },
      claim: { set: 0, row: 0, guardSet: 0 },
      tasks: 1,
      activities: 1,
      assignment: { set: 1, row: 0 },
      statuses: { set: 2, guardSet: 0 },
      commlog: 5,
    },
    smsTemplateTag: undefined,
    textedStatusLookup: false,
  },
  ProcessedPreAuthDetails: {
    detailPageName: 'ProcessedPreAuthDetails',
    taskTypeID: 6,
    followupType: 'Processed PreAuth',
    typeAllCaps: 'PROCESSED_PREAUTHS',
    title: 'PROCESSED_PREAUTHS', // ⚠ today's header really prints this, CreateFollowup.vue:3098
    statusField: 'ProcessedPreAuthStatusID',
    statusLabelField: 'ProcessedPreAuthStatus',
    statusUpdatedAtField: 'ProcessedPreAuthStatusUpdatedAt',
    settings: {
      responsible: 'PROCESSED_PREAUTHS_RESPONSIBLE_EMPLOYEEID',
      maxFollowupsGuard: 'PROCESSED_PREAUTHS_MAX_FOLLOWUPS',
      maxFollowups: 'PROCESSED_PREAUTHS_MAX_FOLLOWUPS',
      autoFrequencyGuard: 'PROCESSED_PREAUTHS_AUTO_FREQUENCY',
      autoFrequency: 'PROCESSED_PREAUTHS_AUTO_FREQUENCY',
      customFrequencyPrefix: 'PROCESSED_PREAUTHS_FOLLOWUP_',
      copyToCommLog: 'PROCESSED_PREAUTHS_COPY_TO_COMMLOG',
      editNoteCopyToCommLog: 'PROCESSED_PREAUTHS_COPY_TO_COMMLOG',
      copyToClaimHistory: null,
    },
    resultSets: {
      patient: { set: 0, row: 2 }, // ⚠ row 2, CreateFollowup.vue:3612
      procedures: { set: 0, guardSet: 0 },
      activities: 1, // ⚠ not the tasks set, CreateFollowup.vue:3761
      statuses: { set: 3, guardSet: 0 },
      tasks: 4,
      commlog: 7,
    },
    smsTemplateTag: undefined,
    textedStatusLookup: false,
  },
  PatientBalancesDetails: {
    detailPageName: 'PatientBalancesDetails',
    taskTypeID: 7,
    followupType: 'Patient Balances',
    typeAllCaps: 'PATIENT_BALANCES',
    title: 'Patient Balances',
    statusField: 'PatientBalancesStatusID',
    statusLabelField: 'PatientBalancesStatus',
    statusUpdatedAtField: 'PatientBalancesStatusUpdatedAt',
    settings: {
      responsible: 'PATIENT_BALANCES_RESPONSIBLE_EMPLOYEEID',
      maxFollowupsGuard: 'PATIENT_BALANCES_MAX_FOLLOWUPS',
      maxFollowups: 'PATIENT_BALANCES_MAX_FOLLOWUPS',
      autoFrequencyGuard: 'PATIENT_BALANCES_AUTO_FREQUENCY',
      autoFrequency: 'PATIENT_BALANCES_AUTO_FREQUENCY',
      customFrequencyPrefix: 'PATIENT_BALANCES_FOLLOWUP_',
      copyToCommLog: 'PATIENT_BALANCES_COPY_TO_COMMLOG',
      editNoteCopyToCommLog: 'PATIENT_BALANCES_COPY_TO_COMMLOG',
      copyToClaimHistory: null,
    },
    resultSets: {
      patient: { set: 0, row: 0, guardSet: 0 },
      balance: { set: 1, row: 0, guardSet: 1, field: 'PatientAmount' },
      familyClaims: 2,
      tasks: 3,
      activities: 3,
      assignment: { set: 3, row: 0 },
      statuses: 4,
      commlog: 6,
    },
    smsTemplateTag: 'patient-balance-follow-up-sms-template',
    textedStatusLookup: true,
  },
};

/** Site-wide settings the drawer reads regardless of queue. */
export const SITE_SETTINGS = {
  timezone: 'GENERAL_TIMEZONE',
  currencySymbol: 'GENERAL_CURRENCY_SYMBOL',
  textingNumber: 'GENERAL_PHONE', // via siteCapability.isSmsEnabled, and the SMS form's banner
  verifiedEmails: 'GENERAL_VERIFIED_EMAILS', // via siteCapability.isEmailEnabled
  paymentEnabled: 'PAYMENT_ENABLED', // '1' / '0' — collectPaymentDisabledReasons, :2942
  remindersPaidFor: 'APPT_REMINDER_PAID_FOR', // 'TRUE' shows the Auto Recall Reminder activity
};

/**
 * The wiki tag the payment template picker filters on (:3061). The EMAIL picker's tag (:3042) is
 * deliberately NOT in this file: whatever lists email templates must also drop review-request pages
 * with wikiTags.withoutReviewRequestTemplates (template-variable-validation's PICKERS guard), and a
 * rule file cannot import it — so the tag lives in the container that does the listing.
 */
export const PAYMENT_TEMPLATE_TAG = 'payment';

/** Today's status lookups by label (:3138, :3153). */
export const TEXTED_STATUS_LABEL = 'TEXTED';
export const EMAILED_STATUS_LABEL = 'EMAILED';

/** The money queues: an editable next date, no Override link, a 1-week default frequency. */
const MONEY_QUEUES = ['AgingDetails', 'OutstandingPreAuthDetails', 'PatientBalancesDetails'];

export function drawerQueueOf(detailPageName) {
  return Object.prototype.hasOwnProperty.call(DRAWER_QUEUES, detailPageName) ? DRAWER_QUEUES[detailPageName] : null;
}

export function isMoneyQueue(detailPageName) {
  return MONEY_QUEUES.indexOf(detailPageName) !== -1;
}

/** One settings row's PropertyValue, or undefined when the row is absent (allSettings shape). */
export function settingValue(allSettings, key) {
  if (!allSettings || !key) return undefined;
  const row = allSettings[key];
  return row ? row.PropertyValue : undefined;
}

/**
 * The value a save sends as `copyToCommLog` (getCommLogProperty, :2666): the setting's value, or ''
 * when the row is absent. `editNote: true` → the note-edit default 'TRUE' (:2775).
 */
export function copyToCommLogOf(detailPageName, allSettings, { editNote } = {}) {
  const q = drawerQueueOf(detailPageName);
  if (!q) return editNote ? 'TRUE' : '';
  const key = editNote ? q.settings.editNoteCopyToCommLog : q.settings.copyToCommLog;
  const row = allSettings ? allSettings[key] : undefined;
  if (row) return row.PropertyValue;
  return editNote ? 'TRUE' : '';
}

/** [maxFollowups, :3256] the raw setting value, or null when absent / settings not loaded. */
export function maxFollowupsOf(detailPageName, allSettings) {
  const q = drawerQueueOf(detailPageName);
  if (!q || !allSettings) return null;
  if (!allSettings[q.settings.maxFollowupsGuard]) return null;
  const row = allSettings[q.settings.maxFollowups];
  if (!row) throw new TypeError(`Cannot read properties of undefined (reading 'PropertyValue') — ${q.settings.maxFollowups}`);
  return row.PropertyValue;
}

/** [responsibleEmployeeID, :3104] the setting split on '|', or null (absent row / no settings). */
export function responsibleEmployeeIdsOf(detailPageName, allSettings) {
  const q = drawerQueueOf(detailPageName);
  if (!q || !allSettings) return null;
  const row = allSettings[q.settings.responsible];
  return row ? row.PropertyValue.split('|') : null;
}

function setOf(data, spec) {
  const index = typeof spec === 'number' ? spec : spec.set;
  return data[index];
}

/** `dataToDisplay[n].length > 0 ? dataToDisplay[n] : null` — the shape most drawer computeds use. */
function nonEmptySet(data, spec) {
  if (spec === undefined) return null;
  const guard = typeof spec === 'number' ? spec : (spec.guardSet !== undefined ? spec.guardSet : spec.set);
  const g = data[guard];
  if (!g || !(g.length > 0)) return null;
  return setOf(data, spec) || null;
}

function singleRow(data, spec) {
  if (spec === undefined) return null;
  if (spec.guardSet !== undefined) {
    const g = data[spec.guardSet];
    if (!g || !(g.length > 0)) return null;
  }
  const s = data[spec.set];
  return s ? (s[spec.row] === undefined ? null : s[spec.row]) : null;
}

/**
 * Everything the drawer reads from the caller's query result (`results.data`, an array of result
 * sets), per queue — the same positions and the same emptiness checks as today's computeds.
 * Absent / empty ⇒ null. `data` null/empty ⇒ null (today's `dataToDisplay` is undefined then and
 * the drawer shows "Unable to Load Patient Data").
 */
export function readDrawerData(detailPageName, data) {
  const q = drawerQueueOf(detailPageName);
  if (!q || !Array.isArray(data) || data.length === 0) return null;
  const rs = q.resultSets;
  const out = {
    patient: singleRow(data, rs.patient),
    tasks: nonEmptySet(data, rs.tasks),
    statuses: nonEmptySet(data, rs.statuses),
    commlog: nonEmptySet(data, rs.commlog),
    claim: rs.claim ? singleRow(data, rs.claim) : (data[0] && data[0].length > 0 ? data[0][0] : null),
  };
  const acts = nonEmptySet(data, rs.activities);
  out.activities = acts ? acts.filter(a => a.ParentTaskID !== null) : null;
  out.mainParentTask = out.tasks ? out.tasks.filter(t => t.ParentTaskID === null)[0] : undefined;
  if (rs.assignment) {
    const s = data[rs.assignment.set];
    out.assignmentRow = s && s.length > 0 ? s[rs.assignment.row] : null;
  }
  ['familyAppts', 'insurance', 'familyMembers', 'patientList', 'pastAppointments', 'familyPendingTx',
    'scheduledAppointments', 'pendingTx', 'txInPlan', 'plannedAppointments', 'familyRecallAppointments',
    'brokenHistory', 'claimStatusHistory', 'familyClaims', 'procedures'].forEach((k) => {
    if (rs[k] !== undefined) out[k] = nonEmptySet(data, rs[k]);
  });
  if (rs.autoRecallReminders !== undefined) out.autoRecallReminders = data[rs.autoRecallReminders];
  if (rs.balance) {
    const row = singleRow(data, rs.balance);
    out.patientBalance = row ? row[rs.balance.field] : undefined;
  }
  return out;
}
